import React, {useEffect, useState} from "react";
import {CSSObject} from "@core-ui/react-stitch";
import {useAppState} from "@core-ui/app-state-provider";
import {
  IconProps,
  OverwriteBrandIconProps,
  ResponsiveIconProps,
} from "../types";

const iconStyles = (fillColor?: string, size?: CSSObject): CSSObject => ({
  display: "inline-block",
  height: size?.height,
  width: size?.width,
  minHeight: size?.height,
  minWidth: size?.width,
  svg: {
    display: "block",
    maxHeight: "100%",
    maxWidth: "100%",
    position: "relative",
    width: "100%",
    path: {
      fill: fillColor,
    },
    rect: {
      fill: fillColor,
    },
  },
});

export const Icon = ({
  brandIconSVGs,
  className,
  crossBrand,
  fillColor,
  size = {
    height: "2rem",
    width: "2rem",
  },
}: IconProps): JSX.Element => {
  const {brandName} = useAppState() || "gap";

  const iconSVG = crossBrand
    ? brandIconSVGs.crossBrand
    : brandIconSVGs[brandName];

  return (
    <span aria-hidden className={className} css={iconStyles(fillColor, size)}>
      {iconSVG}
    </span>
  );
};

export const OverwriteBrandIcon = ({
  brandIconSVG,
  className,
  fillColor,
}: OverwriteBrandIconProps): JSX.Element => (
  <span aria-hidden className={className} css={iconStyles(fillColor)}>
    {brandIconSVG}
  </span>
);

export const ResponsiveIcon = ({
  brandIconSmallViewBoxSVGs,
  brandIconLargeViewBoxSVGs,
  viewboxBreakpoint = 32,
  size,
  ...props
}: ResponsiveIconProps): JSX.Element => {
  const [isLargeViewBox, setIsLargeViewBox] = useState(false);
  const pxToNumber = (px?: string): number => parseInt(px ?? "", 10) || 0;

  useEffect(() => {
    const div = document.createElement("div");
    div.style.display = "none";
    div.style.width = size?.width ?? "0px";
    div.style.height = size?.height ?? "0px";
    document.body.appendChild(div);

    const width = pxToNumber(getComputedStyle(div).getPropertyValue("width"));
    const height = pxToNumber(getComputedStyle(div).getPropertyValue("height"));

    div.parentNode?.removeChild(div);

    if (width >= viewboxBreakpoint || height >= viewboxBreakpoint) {
      setIsLargeViewBox(true);
    } else {
      setIsLargeViewBox(false);
    }
  }, [size]);

  return (
    <Icon
      {...props}
      brandIconSVGs={
        isLargeViewBox ? brandIconLargeViewBoxSVGs : brandIconSmallViewBoxSVGs
      }
      size={size}
    />
  );
};
